import React, { useState, useEffect, useRef } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { Info } from "lucide-react";
import { Button } from "./ui/button";

const CockpitMenu = ({
  isOpen,
  onClose,
  onParametersChange,
  initialParameters,
}) => {
  const [parameters, setParameters] = useState({
    temperature: 0.7,
    top_p: 1,
    max_tokens: 2048,
    frequency_penalty: 0,
    presence_penalty: 0,
  });

  // Use a ref to track if we've initialized from props
  const initializedRef = useRef(false);

  // Only initialize from props once
  useEffect(() => {
    if (initialParameters && !initializedRef.current) {
      setParameters((prev) => ({
        ...prev,
        ...initialParameters,
      }));
      initializedRef.current = true;
    }
  }, [initialParameters]);

  // Use a ref to prevent continuous updates
  const parametersRef = useRef(parameters);

  // Update the ref when parameters change
  useEffect(() => {
    parametersRef.current = parameters;
  }, [parameters]);

  // Debounce the parameter changes to parent
  useEffect(() => {
    if (!onParametersChange) return;

    const timer = setTimeout(() => {
      onParametersChange(parametersRef.current);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [parameters, onParametersChange]);

  const handleParameterChange = (name, value) => {
    // Ensure numeric values are properly converted
    let processedValue = value;

    if (
      name === "temperature" ||
      name === "top_p" ||
      name === "frequency_penalty" ||
      name === "presence_penalty"
    ) {
      processedValue = parseFloat(value);
    } else if (name === "max_tokens") {
      processedValue = parseInt(value);
    }

    console.log(
      `Setting ${name} to ${processedValue} (${typeof processedValue})`
    );

    setParameters((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  const parameterDefinitions = [
    {
      name: "temperature",
      label: "Temperature",
      description: "Controls randomness of responses.",
      range: "0 to 1",
      type: "range",
      min: 0,
      max: 1,
      step: 0.1,
    },
    {
      name: "top_p",
      label: "Top_p",
      description: "Limits token selection to top probability.",
      range: "0 to 1",
      type: "range",
      min: 0,
      max: 1,
      step: 0.1,
    },
    {
      name: "max_tokens",
      label: "Max Tokens",
      description: "Sets maximum response length.",
      range: "Positive integer",
      type: "number",
      min: 1,
      max: 4096,
    },
    {
      name: "frequency_penalty",
      label: "Frequency Penalty",
      description: "Discourages word repetition.",
      range: "-2 to 2",
      type: "range",
      min: -2,
      max: 2,
      step: 0.1,
    },
    {
      name: "presence_penalty",
      label: "Presence Penalty",
      description: "Encourages new topics.",
      range: "-2 to 2",
      type: "range",
      min: -2,
      max: 2,
      step: 0.1,
    },
  ];

  if (!isOpen) return null;

  return (
    <TooltipProvider>
      <div className="cockpit-menu">
        {parameterDefinitions.map((param) => (
          <div key={param.name} className="cockpit-parameter">
            <div className="parameter-header">
              <span className="parameter-name">{param.label}</span>
              <div className="info-tooltip">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 p-0"
                      aria-label={`Info about ${param.label}`}
                    >
                      <Info className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{param.description}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="parameter-input">
              {param.type === "range" || param.type === "number" ? (
                <input
                  type={param.type}
                  min={param.min}
                  max={param.max}
                  step={param.step}
                  value={parameters[param.name]}
                  onChange={(e) =>
                    handleParameterChange(
                      param.name,
                      param.type === "number"
                        ? parseInt(e.target.value)
                        : parseFloat(e.target.value)
                    )
                  }
                />
              ) : (
                <input
                  type="text"
                  placeholder={param.placeholder}
                  value={parameters[param.name]}
                  onChange={(e) =>
                    handleParameterChange(param.name, e.target.value)
                  }
                />
              )}
              {param.type === "range" && (
                <span className="range-value">{parameters[param.name]}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
};

export default CockpitMenu;
